import {
  type AmplifyError,
  confirmSignIn,
} from '@backoffice-frontend/authentication'
import { FormPasswordfield } from '@backoffice-frontend/forms'
import { MoiaGrid, useTheme } from '@backoffice-frontend/patterns'
import { usePaceValidators } from '@backoffice-frontend/validators'
import { Button, Callout, LoadingIndicator, Text } from '@moia-dev/pace-web'
import type { ConfirmSignInOutput } from 'aws-amplify/auth'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type PasswordChallengeFormValues = {
  newPassword: string
  newPasswordRepeat: string
}

export const PasswordChallengeForm = ({
  isMobile,
  onSuccess,
  onClose,
}: {
  isMobile: boolean
  onSuccess: (result: ConfirmSignInOutput) => void
  onClose: VoidFunction
}) => {
  const { t } = useTranslation()
  const { handleSubmit, control, formState, setError } =
    useForm<PasswordChallengeFormValues>({
      mode: 'onChange',
      defaultValues: {
        newPassword: '',
        newPasswordRepeat: '',
      },
    })
  const theme = useTheme()

  const { required } = usePaceValidators()

  const onSubmit = async ({
    newPassword,
    newPasswordRepeat,
  }: PasswordChallengeFormValues) => {
    if (newPasswordRepeat !== newPassword) {
      setError('newPasswordRepeat', {
        type: 'validate',
        message: t('common_newPasswordMismatch_error'),
      })
      return
    }
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      })
      onSuccess(result)
    } catch (_error) {
      const error = _error as AmplifyError
      if (error.name === 'LimitExceededException') {
        setError('root.serverError', {
          message: t('loginView_resetPasswordLimitExceededError'),
        })
      } else if (error.name === 'InvalidPasswordException') {
        setError('newPassword', {
          type: 'validate',
          message: t('common_passwordPolicy_error'),
        })
      } else {
        setError('root.serverError', {
          message: t('loginView_validationErrorMessage'),
        })
      }
    }
  }

  return (
    <>
      {formState.errors.root?.serverError && (
        <div
          css={theme => ({
            marginTop: isMobile ? theme.space.Space2 : theme.space.Space6,
          })}
        >
          <Callout variant="neutral">
            {formState.errors.root.serverError.message}
          </Callout>
        </div>
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          marginTop: isMobile ? theme.space.Space2 : theme.space.Space6,
        }}
      >
        <Text size="sm" css={{ paddingBottom: `${theme.space.Space2}` }}>
          <Text weight="bold">{t('common_passwordPolicy_header')}</Text>
          <Text
            css={{
              whiteSpace: 'pre-line',
              display: 'block',
              paddingLeft: theme.space.Space2,
            }}
          >
            {t('common_passwordPolicy')}
          </Text>
        </Text>
        <MoiaGrid mt={1} gridGap={isMobile ? 1 : 3}>
          <FormPasswordfield
            control={control}
            name="newPassword"
            label={t('loginView_newPassword_label')}
            rules={{
              required,
            }}
            removeSpaces
          />
          <FormPasswordfield
            control={control}
            name="newPasswordRepeat"
            label={t('loginView_newPasswordRepeat_label')}
            rules={{
              required,
            }}
            removeSpaces
          />
          <MoiaGrid
            gridTemplateColumns="max-content max-content"
            justifyContent="end"
            gridGap={1}
          >
            <Button
              type="button"
              onClick={onClose}
              label={t('common_back_button')}
            />
            <Button
              type="submit"
              disabled={!formState.isValid}
              color="primary"
              icon={formState.isSubmitting ? <LoadingIndicator /> : undefined}
              label={t('common_submit_button')}
            />
          </MoiaGrid>
        </MoiaGrid>
      </form>
    </>
  )
}
